import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../config/apiService';

// SIP monthly points — period-picker / back-fill model (calculated points).
//  - Pick a month (period). If no period exists for it, create one (back-fill
//    Nov 2025 onward). Only one uncommitted period can exist at a time, which
//    keeps the 4->8 ramp committing in chronological order.
//  - Points are calculated (eligible ? min(prev+4,8) : 0); not hand-entered.
//  - Each site group answers "Recordable during period?" before commit.
//  - A committed month is read-only (no overwrite).
function DisbursementManager() {
    // Default to LAST month — the reporting period admins manage this month.
    const lastMonth = new Date();
    lastMonth.setDate(1);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const [yyyymm, setYyyymm] = useState(`${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`);
    const [year, month] = useMemo(() => yyyymm.split('-').map(Number), [yyyymm]);

    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(null);   // { periodExists, completed, sipDisbursementDateID, date }
    const [entries, setEntries] = useState([]);
    const [filterGroup, setFilterGroup] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');
    const [commitStatus, setCommitStatus] = useState([]);  // per-site qual commit status

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };
    const monthLabel = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });

    const reload = async () => {
        setLoading(true); setMsg('');
        try {
            const res = await adminAPI.getDisbursementByMonth(year, month);
            setPeriod(res.data?.period || null);
            setEntries(res.data?.entries || []);
            try {
                const cs = await adminAPI.getPeriodCommitStatus(year, month);
                setCommitStatus(cs.data || []);
            } catch { setCommitStatus([]); }
        } catch {
            flash('Failed to load disbursement.', 'error');
        } finally { setLoading(false); }
    };

    // On first open, lazily auto-create last month's period (7th/15th dates,
    // seeded supervisors) so admins can start managing it without a manual
    // create. Only the default month is ensured, not arbitrary browsed months.
    useEffect(() => {
        (async () => {
            try { await adminAPI.ensurePeriod(lastMonth.getFullYear(), lastMonth.getMonth() + 1); } catch { /* non-fatal */ }
            reload();
        })();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [yyyymm]);

    const groups = useMemo(() => {
        const map = new Map();
        for (const e of entries) {
            const key = e.siteGroupKey || '(not in program)';
            if (!map.has(key)) map.set(key, { key, inProgram: !!e.siteGroupKey, rows: [] });
            map.get(key).rows.push(e);
        }
        return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
    }, [entries]);

    const groupAnswer = (g) => {
        const v = g.rows.find((r) => r.noReportableForMonth !== null && r.noReportableForMonth !== undefined)?.noReportableForMonth;
        if (v === null || v === undefined) return 'unanswered';
        return v ? 'no' : 'yes';
    };
    const allInProgramAnswered = groups.filter((g) => g.inProgram).every((g) => groupAnswer(g) !== 'unanswered');

    const committed = !!period?.completed;
    const exists = !!period?.periodExists;

    // Every site must have committed its supervisor qualifications first.
    const pendingSites = commitStatus.filter((s) => !s.isCommitted);
    const allSitesCommitted = commitStatus.length > 0 && pendingSites.length === 0;

    const createPeriod = async () => {
        // Default period date = last day of the selected month (the supervisor
        // qualification deadline; adjust the convention later if needed).
        const lastDay = new Date(year, month, 0);
        const dateStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
        if (!window.confirm(`Create the ${monthLabel} period? (Only one uncommitted period can exist at a time.)`)) return;
        setBusy(true); setMsg('');
        try { await adminAPI.createPeriod(dateStr); flash('Period created.', 'success'); await reload(); }
        catch (err) { flash(extractError(err, 'Failed to create period.'), 'error'); }
        finally { setBusy(false); }
    };

    const answerReportable = async (groupKey, reportable) => {
        const label = reportable ? 'YES — a recordable happened (this zeroes the group)' : 'No — clean month';
        if (!window.confirm(`Set ${groupKey}: ${label}?`)) return;
        setBusy(true); setMsg('');
        try {
            await adminAPI.setReportable({ periodId: period.sipDisbursementDateID, siteGroupKey: groupKey, reportable });
            flash(`${groupKey} recordable answered.`, 'success');
            await reload();
        } catch (err) { flash(extractError(err, 'Failed to set recordable.'), 'error'); }
        finally { setBusy(false); }
    };

    const commit = async () => {
        if (!allInProgramAnswered) { flash('Answer the recordable question for every site group first.', 'error'); return; }
        if (!window.confirm(`Commit ${monthLabel}?\n\nThis awards calculated points and closes the month (no overwrite afterward).`)) return;
        setBusy(true);
        try { await adminAPI.commit(period.sipDisbursementDateID); flash('Period committed.', 'success'); await reload(); }
        catch (err) { flash(extractError(err, 'Commit failed.'), 'error'); }
        finally { setBusy(false); }
    };

    const deletePending = async () => {
        if (!window.confirm(`Delete the uncommitted ${monthLabel} period? All entered data for it is lost.`)) return;
        setBusy(true);
        try { await adminAPI.deletePending(); flash('Pending period deleted.', 'success'); await reload(); }
        catch (err) { flash(extractError(err, 'Delete failed.'), 'error'); }
        finally { setBusy(false); }
    };

    const visibleGroups = filterGroup ? groups.filter((g) => g.key === filterGroup) : groups;

    return (
        <div>
            <div style={styles.card}>
                <div style={styles.eyebrow}>Monthly Points</div>
                <div style={styles.bigDate}>{monthLabel}</div>
                <div style={styles.bulkRow}>
                    <label style={styles.bulkLabel}>Period:</label>
                    <input type="month" value={yyyymm} onChange={(e) => setYyyymm(e.target.value)} style={styles.input} />
                    {exists && (
                        <span style={committed ? styles.badgeDone : styles.badgePending}>
                            {committed ? 'Committed' : 'Open'}
                        </span>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        {exists && !committed && (
                            <>
                                <button onClick={commit} disabled={busy || !allInProgramAnswered || !allSitesCommitted} style={btn('#15803d', busy || !allInProgramAnswered || !allSitesCommitted)}>Commit period</button>
                                <button onClick={deletePending} disabled={busy} style={btn('#dc2626', busy)}>Delete</button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div style={{ ...styles.card, marginTop: 12 }}><div style={styles.muted}>Loading…</div></div>
            ) : !exists ? (
                <div style={{ ...styles.card, marginTop: 12 }}>
                    <h2 style={styles.cardTitle}>No period for {monthLabel}</h2>
                    <p style={styles.muted}>Create it to enter recordable answers and commit (used for back-filling past months).</p>
                    <button onClick={createPeriod} disabled={busy} style={btn('#15803d', busy)}>Create {monthLabel} period</button>
                </div>
            ) : (
                <>
                    {committed && <Banner kind="success">This month is committed and read-only — points have been awarded.</Banner>}
                    {!committed && commitStatus.length > 0 && (
                        <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: allSitesCommitted ? '#f0fdf4' : '#fffbeb', border: `1px solid ${allSitesCommitted ? '#bbf7d0' : '#fde68a'}`, fontSize: 13 }}>
                            <strong>Qualification commit status:</strong>{' '}
                            {commitStatus.map((s) => (
                                <span key={s.siteID} style={{ marginRight: 10, color: s.isCommitted ? '#15803d' : '#b45309', fontWeight: 600 }}>
                                    {s.isCommitted ? '✓' : '○'} {s.site}
                                </span>
                            ))}
                            {!allSitesCommitted && <div style={{ color: '#b45309', marginTop: 4 }}>Commit is locked until every site commits its supervisor qualifications (Qualification Maintenance → Save &amp; Commit).</div>}
                        </div>
                    )}
                    {!committed && !allInProgramAnswered && (
                        <Banner kind="info">
                            Commit is locked until every site group answers the recordable question —
                            answer it on <strong>Qualification Maintenance</strong> (Leader&rsquo;s program).
                        </Banner>
                    )}

                    <div style={{ ...styles.card, marginTop: 12 }}>
                        <div style={styles.bulkRow}>
                            <div style={styles.bulkLabel}>Filter by site group:</div>
                            <select value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)} style={styles.input}>
                                <option value="">All</option>
                                {groups.map((g) => <option key={g.key} value={g.key}>{g.key}</option>)}
                            </select>
                        </div>
                    </div>

                    {msg && <Banner kind={msgKind}>{msg}</Banner>}

                    {visibleGroups.map((g) => {
                        const ans = groupAnswer(g);
                        return (
                            <div key={g.key} style={{ ...styles.card, marginTop: 12 }}>
                                <div style={styles.groupHeader}>
                                    <div style={styles.groupName}>{g.key}</div>
                                    {/* Read-only here — the question is answered on
                                        Qualification Maintenance (Leader). */}
                                    {g.inProgram ? (
                                        <span style={styles.muted}>
                                            Recordable:{' '}
                                            <strong style={{ color: ans === 'yes' ? '#dc2626' : ans === 'no' ? '#15803d' : '#b45309' }}>
                                                {ans === 'yes' ? 'Yes' : ans === 'no' ? 'No' : 'not answered'}
                                            </strong>
                                        </span>
                                    ) : (
                                        <span style={styles.muted}>not in program</span>
                                    )}
                                </div>

                                {ans === 'yes' && (
                                    <Banner kind="error">Recordable recorded — every supervisor and employee in {g.key} earns 0 this period.</Banner>
                                )}

                                <table style={styles.table}>
                                    <thead style={styles.thead}>
                                        <tr>
                                            <th style={styles.th}>Name</th>
                                            <th style={styles.th}>Site</th>
                                            <th style={{ ...styles.th, textAlign: 'right' }}>Previous</th>
                                            <th style={{ ...styles.th, textAlign: 'center' }}>Eligible</th>
                                            <th style={{ ...styles.th, textAlign: 'right' }}>{committed ? 'Awarded' : 'Points (calculated)'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {g.rows.map((e) => {
                                            const pts = committed ? (e.newPoints ?? 0) : (e.calculatedPoints ?? 0);
                                            return (
                                                <tr key={e.sipPointDisbursementID} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                    <td style={styles.td}>{e.lastName}{e.lastName && e.firstName ? ', ' : ''}{e.firstName}</td>
                                                    <td style={styles.td}>{e.site || '—'}</td>
                                                    <td style={{ ...styles.td, textAlign: 'right', color: '#6b7280' }}>{e.previousPoints ?? 0}</td>
                                                    <td style={{ ...styles.td, textAlign: 'center' }}><span style={badge(e.eligible)}>{e.eligible ? 'Yes' : 'No'}</span></td>
                                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: pts > 0 ? '#047857' : '#9ca3af' }}>{pts}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#eff6ff';
    return <div style={{ margin: '10px 0 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

function extractError(err, fallback) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data) return data;
    if (data?.message) return data.message;
    return fallback;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});
const toggleBtn = (active, activeColor) => ({
    background: active ? activeColor : 'white', color: active ? 'white' : '#374151',
    border: `1px solid ${active ? activeColor : '#d1d5db'}`, padding: '5px 14px', borderRadius: 4,
    fontWeight: 700, fontSize: 13, cursor: 'pointer'
});
const badge = (ok) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
    background: ok ? '#dcfce7' : '#f3f4f6', color: ok ? '#15803d' : '#6b7280'
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    cardTitle: { marginTop: 0, marginBottom: 6, fontSize: 18, fontWeight: 800, color: '#111827' },
    muted: { color: '#6b7280', fontSize: 13 },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    bigDate: { fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 2, marginBottom: 8 },
    bulkRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    bulkLabel: { fontSize: 12, fontWeight: 700, color: '#374151' },
    input: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
    badgeDone: { padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d' },
    badgePending: { padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#fef9c3', color: '#854d0e' },
    groupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 8 },
    groupName: { fontSize: 16, fontWeight: 800, color: '#111827' },
    reportableRow: { display: 'flex', alignItems: 'center', gap: 6 },
    reportableLabel: { fontSize: 13, fontWeight: 600, color: '#374151', marginRight: 4 },
    unanswered: { fontSize: 12, color: '#b45309', fontWeight: 600, marginLeft: 4 },
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    th: { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '8px 12px', fontSize: 14, color: '#111827' }
};

export default DisbursementManager;
